// Homepage.js
import React, { useEffect, useState } from 'react';
import QRCodeComponent from '../utils/QRCodeComponent'; // Import the QR code component
import CreateCustomUrl from './CreateCustomUrl';
import { useNavigate } from 'react-router-dom';
import './Homepage.css';
import { getAuthHeader, getStoredToken, isTokenExpired } from '../utils/tokenManager';

const apiUrl = process.env.REACT_APP_DJANGO_API_URL;
const webUrl = process.env.REACT_APP_WEB_URL;

console.log(apiUrl)

const Homepage = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState({}); // Track copy state for each device
  const [selectedUsers, setSelectedUsers] = useState({});
  const [customUrls, setCustomUrls] = useState({});
  const [expandedDevice, setExpandedDevice] = useState(null);

  const checkTokenExpiration = () => {
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return false;
    }

    if (isTokenExpired()) {
      // Check which token exists and remove only that one
      if (localStorage.getItem('token')) {
        localStorage.removeItem('token');
      } else if (localStorage.getItem('apple_token')) {
        localStorage.removeItem('apple_token');
        localStorage.removeItem('apple_refresh_token');
        localStorage.removeItem('apple_token_expires_at');
      }
      navigate('/login');
      return false;
    }
    return true;
  };

  const handleSaveCustomUrl = async (device) => {
    if (!checkTokenExpiration()) return;
    
    const customUrl = customUrls[device.account_id];
    if (!customUrl || customUrl.error || !customUrl.value) {
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/${device.account_id}/update_custom_account_id/?custom_account_id=${encodeURIComponent(customUrl.value)}`, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to update custom ID');
      }

      // Refresh the devices list to show the updated custom ID
      const fetchDevices = async () => {
        try {
          const response = await fetch(`${apiUrl}/api/home_page/`, {
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeader()
            },
          });
          if (!response.ok) {
            setError(true);
            throw new Error(`Error: ${response.status}`);
          }
          const data = await response.json();
          setDevices(data['devices']);
        } catch (err) {
          console.error("Fetch error:", err);
          setError(true);
        }
      };
      
      await fetchDevices();
    } catch (error) {
      console.error('Error saving custom ID:', error);
    }
  };

  useEffect(() => {
    const fetchDevices = async () => {
      if (!checkTokenExpiration()) return;
      
      try {
        const response = await fetch(`${apiUrl}/api/home_page/`, {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader()
          },
        });
        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem("token");
            navigate('/login');
            return;
          }
          setError(true);
          throw new Error(`Error: ${response.status}`);
        }
        const data = await response.json();
        setDevices(data['devices']);
        
        const initialSelectedUsers = {};
        const initialCustomUrls = {};
        const initialCopiedStates = {};
        data['devices'].forEach(device => {
          initialSelectedUsers[device.account_id] = device.user_ids[0] || '1';
          initialCustomUrls[device.account_id] = {
            showInput: false,
            value: device.custom_account_id || '',
            error: false,
            errorMessage: ''
          };
          initialCopiedStates[device.account_id] = false;
        });
        setSelectedUsers(initialSelectedUsers);
        setCustomUrls(initialCustomUrls);
        setCopied(initialCopiedStates);
        
        // If there's only one device, expand it automatically
        if (data['devices'].length === 1) {
          setExpandedDevice(data['devices'][0].account_id);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(true);
      }
    };
    fetchDevices();
  }, [navigate]);

  const getDeviceUrl = (device, selectedUser) => {
    const baseUrl = `${webUrl}/${device.custom_account_id || device.account_id}`;
    return selectedUser ? `${baseUrl}/?user_id=${selectedUser}` : `${baseUrl}/`;
  };

  const getOnboardingUrl = (device, selectedUser) => {
    const baseUrl = `${webUrl}/${device.account_id}/onboarding`;
    return selectedUser ? `${baseUrl}/?user_id=${selectedUser}` : baseUrl;
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(prev => ({
        ...prev,
        [url]: true
      }));
      setTimeout(() => {
        setCopied(prev => ({
          ...prev,
          [url]: false
        }));
      }, 2000);
    });
  };

  const handleUserChange = (deviceId, userId) => {
    setSelectedUsers(prev => ({
      ...prev,
      [deviceId]: userId
    }));
  };

  const handleCustomUrlChange = (accountId, newCustomUrl) => {
    setCustomUrls(prev => ({
      ...prev,
      [accountId]: newCustomUrl
    }));
  };

  const toggleDevice = (deviceId) => {
    setExpandedDevice(expandedDevice === deviceId ? null : deviceId);
  };

  const renderDeviceDetails = (device) => {
    const canAddUser = device.max_users > 0 && device.current_user_count < device.max_users;
    const userUrl = getDeviceUrl(device, selectedUsers[device.account_id]);
    const accountUrl = getDeviceUrl(device);

    return (
      <div className="device-details">
        <div className="detail-row">
          <label>Users:</label>
          <div className="detail-content">
            {device.user_ids.length > 0 ? (
              <div className="user-info">
                <select
                  className="user-select"
                  value={selectedUsers[device.account_id]}
                  onChange={(e) => handleUserChange(device.account_id, e.target.value)}
                >
                  {device.user_ids.map(userId => (
                    <option key={userId} value={userId}>
                      {device.display_names[userId] || `User ${userId}`}
                    </option>
                  ))}
                </select>
                <div className="user-count">
                  {device.current_user_count} / {device.max_users} users
                </div>
                {canAddUser && (
                  <a 
                    href={getOnboardingUrl(device, device.current_user_count+1)}
                    className="add-user-btn"
                  >
                    Add User
                  </a>
                )}
              </div>
            ) : (
              <span>No users available</span>
            )}
          </div>
        </div>

        <div className="detail-row">
          <label>Custom ID:</label>
          <div className="detail-content">
            <CreateCustomUrl
              onCustomUrlSelect={(newUrl) => handleCustomUrlChange(device.account_id, newUrl)}
              selectedCustomUrl={customUrls[device.account_id] || { showInput: false, value: '', error: false, errorMessage: '' }}
              accountId={device.account_id}
              inline={true}
              onSave={() => handleSaveCustomUrl(device)}
            />
          </div>
        </div>

        <div className="detail-row">
          <label>Share Links:</label>
          <div className="detail-content">
            {device.max_users > 1 && (
              <div className="share-link-item">
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(userUrl)}
                >
                  Copy Link to User Share Page
                </button>
                {copied[userUrl] && <span className="copy-feedback">Copied!</span>}
              </div>
            )}
            <div className="share-link-item">
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(accountUrl)}
              >
                Copy Link to Account Share Page
              </button>
              {copied[accountUrl] && <span className="copy-feedback">Copied!</span>}
            </div>
          </div>
        </div>

        <div className="detail-row">
          <label>QR Code:</label>
          <div className="detail-content">
            <QRCodeComponent url={userUrl} />
          </div>
        </div>

        <div className="detail-row">
          <label>Actions:</label>
          <div className="detail-content">
            <a 
              href={getOnboardingUrl(device, selectedUsers[device.account_id])}
              target="_blank"
              rel="noopener noreferrer"
              className="update-link"
            >
              Update
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="homepage-container">
      <h1>Device Management</h1>
      {error ? (
        <p>Error loading data. Please try again.</p>
      ) : (
        <div className="devices-list">
              {devices.map((device) => (
            <div key={device.account_id} className="device-card">
                  <button
                className={`device-header ${expandedDevice === device.account_id ? 'expanded' : ''}`}
                onClick={() => toggleDevice(device.account_id)}
              >
                <span className="device-title">
                  {device.custom_account_id || device.account_id}
                </span>
                <span className="expand-icon">
                  {expandedDevice === device.account_id ? '▼' : '▶'}
                </span>
                    </button>
              {expandedDevice === device.account_id && renderDeviceDetails(device)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Homepage;
